import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, API } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { Button } from "../components/ui/button";
import { ArrowLeft, FileDown, ArrowRight, Copy, Trash2 } from "lucide-react";
import DocumentEditor from "../components/DocumentEditor";
import SendEmailDialog from "../components/SendEmailDialog";
import { toast } from "sonner";

const STATUSES = ["draft", "sent", "accepted", "rejected", "converted"];

export default function QuoteEditor() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { id } = useParams();
  const isNew = id === "new";
  const [existing, setExisting] = useState(null);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/clients").then((r) => setClients(r.data));
    if (!isNew) {
      api.get(`/quotes/${id}`).then((r) => {
        setExisting(r.data);
        setLoading(false);
      });
    } else {
      setLoading(false);
    }
  }, [id, isNew]);

  const onSaved = (doc) => {
    if (isNew) navigate(`/app/quotes/${doc.id}`, { replace: true });
    else setExisting(doc);
  };

  const downloadPdf = () => {
    if (!existing) return;
    const token = localStorage.getItem("hosys_token");
    const url = `${API}/quotes/${id}/pdf?token=${encodeURIComponent(token)}`;
    window.open(url, "_blank", "noopener");
  };

  const convert = async () => {
    const r = await api.post(`/quotes/${id}/convert`);
    toast.success("Convertit a factura!");
    navigate(`/app/invoices/${r.data.id}`);
  };

  const duplicate = async () => {
    const r = await api.post(`/quotes/${id}/duplicate`);
    navigate(`/app/quotes/${r.data.id}`);
  };

  const remove = async () => {
    if (!window.confirm("Eliminar?")) return;
    await api.delete(`/quotes/${id}`);
    navigate("/app/quotes");
  };

  if (loading) return <div className="text-slate-400">{t("common.loading")}</div>;

  return (
    <div className="space-y-6" data-testid="quote-editor-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <Link to="/app/quotes" className="text-slate-400 hover:text-white shrink-0">
            <ArrowLeft size={20} />
          </Link>
          <div className="min-w-0">
            <h1 className="font-display text-xl sm:text-2xl lg:text-3xl font-bold tracking-tighter truncate">
              {isNew ? t("editor.new_quote") : `${t("editor.edit_quote")} · ${existing?.number}`}
            </h1>
          </div>
        </div>
        {!isNew && existing && (
          <div className="flex flex-wrap items-center gap-2">
            <SendEmailDialog
              kind="quote"
              docId={existing.id}
              docNumber={existing.number}
              clientEmail={clients.find((c) => c.id === existing.client_id)?.email}
              clientName={clients.find((c) => c.id === existing.client_id)?.name}
            />
            <Button variant="outline" size="sm" onClick={downloadPdf} className="border-white/10" data-testid="quote-download-pdf">
              <FileDown size={14} className="mr-1.5" /> PDF
            </Button>
            <Button variant="outline" size="sm" onClick={duplicate} className="border-white/10">
              <Copy size={14} className="mr-1.5" /> {t("quotes.duplicate")}
            </Button>
            {existing.status !== "converted" && (
              <Button size="sm" onClick={convert} className="bg-emerald-600 hover:bg-emerald-500" data-testid="quote-convert-btn">
                <ArrowRight size={14} className="mr-1.5" /> {t("quotes.convert")}
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={remove} className="border-rose-500/30 text-rose-300 hover:bg-rose-500/10">
              <Trash2 size={14} />
            </Button>
          </div>
        )}
      </div>

      <DocumentEditor
        kind="quote"
        existing={existing}
        clients={clients}
        onSaved={onSaved}
        statusOptions={STATUSES}
      />
    </div>
  );
}
